import { Button, Flex, Text } from '@instamenu/ui-system-next';
import { SelectionModal } from '@instamenu/ui-system-next';
import { Address, useUserContext } from '@instamenu/context-user';
import { Checkout } from '@instamenu/context-checkout';
import { Pencil, Trash } from 'lucide-react';
import { palette } from '@instamenu/ui-vanilla-extract';
import { truncate } from '@instamenu/formatters';

type AddressListProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setShowModal: (showModal: boolean) => void;
  selectedAddress: Address | null;
  setSelectedAddress: (address: Address | null) => void;
  checkout: Checkout;
  handleClose: () => void;
  setToEdit: (address: Address | undefined) => void;
  setShowAddressForm: (showAddressForm: boolean) => void;
};

const AddressList = ({ ...props }: AddressListProps) => {
  const { user, setShowModal } = useUserContext();
  const {
    selectedAddress,
    setSelectedAddress,
    handleClose,
    checkout,
    setToEdit,
    setShowAddressForm,
    setIsOpen,
  } = props;

  const handleConfirm = () => {
    if (selectedAddress) {
      checkout?.addresses.setAddress(selectedAddress);
      handleClose();
    }
  };
  const handleAddNew = () => {
    if (user) {
      setShowAddressForm(true);
    } else {
      setShowModal(true);
      setIsOpen(false);
    }
  };
  const renderAddressActions = (address: Address) =>
    !!address.id && (
      <Flex style={{ gap: 8 }}>
        <Button
          variant="text"
          onClick={() => {
            setToEdit(address);
            setShowAddressForm(true);
          }}
        >
          <Pencil width={18} height={18} color={palette.red600} />
        </Button>
        <Button
          variant="text"
          data-id="delete-address-button"
          onClick={() => address.id && checkout.addresses.discard(address)}
        >
          <Trash size={18} color={palette.red600} />
        </Button>
      </Flex>
    );

  const renderAddressItem = (address: Address) => (
    <div style={{ flex: 1 }}>
      <Text css={{ flex: 1 }} wordBreak maxLines={1} variant="caption1">
        {address.line1}
      </Text>
      <Text css={{ flex: 1 }} wordBreak maxLines={1} variant="caption2">
        {address.line2}
      </Text>
      {address.specialInstructions && (
        <Text wordBreak css={{ flex: 1 }} maxLines={1} variant="caption2">
          {truncate(address.specialInstructions, 60)}
        </Text>
      )}
    </div>
  );

  return (
    <SelectionModal
      opened
      title="Select an address"
      options={checkout.addresses.book}
      value={selectedAddress ? selectedAddress : checkout.order.deliveryAddress || null}
      onChange={setSelectedAddress}
      onConfirm={handleConfirm}
      onAddNew={handleAddNew}
      addNewButtonText={user ? '+ Add new address' : 'Sign in to add a new address'}
      confirmButtonText="Update"
      onClose={handleClose}
      renderItem={renderAddressItem}
      renderActions={renderAddressActions}
      data-testid="address-selection-modal"
    />
  );
};

export default AddressList;
